// components/auth/OtpVerify.tsx

"use client";
import { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from "react";

interface OtpInputProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  error?: string;
  loading?: boolean;
}

export default function OtpVerify({ email, onVerify, onResend, onBack, error, loading }: OtpInputProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [resendSec, setResendSec] = useState(60);
  const [resending, setResending] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown
  useEffect(() => {
    if (resendSec <= 0) return;
    const t = setInterval(() => setResendSec((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendSec]);

  const updateDigit = (i: number, val: string) => {
    const d = [...digits];
    d[i] = val.replace(/\D/g, "").slice(-1);
    setDigits(d);
    if (d[i] && i < 5) refs.current[i + 1]?.focus();
    if (d.every(Boolean)) {
      onVerify(d.join(""));
    }
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    const d = [...digits];
    text.split("").forEach((ch, i) => { if (i < 6) d[i] = ch; });
    setDigits(d);
    refs.current[Math.min(text.length, 5)]?.focus();
    if (d.every(Boolean)) onVerify(d.join(""));
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
      setResendSec(60);
      setDigits(Array(6).fill(""));
      refs.current[0]?.focus();
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Icon */}
      <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center mb-6">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-700">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
        </svg>
      </div>

      <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-1">
        Проверьте почту
      </h1>
      <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
        Мы отправили 6-значный код на{" "}
        <span className="font-medium text-zinc-700">{email}</span>.<br />
        Код действителен 10 минут.
      </p>

      {/* OTP inputs */}
        <div className="grid grid-cols-6 gap-2 sm:gap-3 w-full max-w-[340px] mx-auto mb-8">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => updateDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              disabled={loading}
              className={`
                w-12 h-12 sm:w-14 sm:h-14 text-center text-xl font-semibold rounded-xl border
                transition-all outline-none shadow-sm
                ${d ? "border-zinc-900 bg-zinc-50 shadow-md" : "border-zinc-200 bg-white"}
                ${error ? "border-red-400 bg-red-50 text-red-600" : "text-zinc-900"}
                focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/10 focus:-translate-y-0.5
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            />
          ))}
        </div>

      {error && (
        <p className="text-sm text-red-500 mb-4 flex items-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
          </svg>
          {error}
        </p>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
          <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
          Проверяем код...
        </div>
      )}

      {/* Resend */}
      <div className="text-sm text-zinc-500">
        {resendSec > 0 ? (
          <span>Повторная отправка через <span className="font-medium text-zinc-700 tabular-nums">{resendSec}с</span></span>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-zinc-900 font-medium underline underline-offset-2 hover:no-underline disabled:opacity-50"
          >
            {resending ? "Отправляем..." : "Отправить повторно"}
          </button>
        )}
      </div>

      <button
        onClick={onBack}
        className="mt-6 flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m15 18-6-6 6-6"/>
        </svg>
        Вернуться назад
      </button>
    </div>
  );
}